import type { Controls, TouchVerb } from "./controls";

/*
 * Two thumbs, and the buttons between them.
 *
 * The mobile compatibility pass (the owner's report of 2026-08-29: *"the screen
 * with instructions I cannot click"*). A phone has no pointer lock, no keyboard
 * and no mouse wheel, and the client asked for all three — so this file gives a
 * touchscreen the same three things the desktop already had, through the same
 * door: **a walk, a look, and a verb**. Nothing here decides anything about the
 * game. It measures a finger and calls `Controls.setStick`, `Controls.pushLook`
 * or `Controls.press`, which are the fields the keys and the mouse write
 * (`input/controls.ts`).
 *
 * Three things about the shape are worth stating, because none of them is
 * obvious from the code:
 *
 * - **The sticks listen on the canvas, not on a layer of their own.** A
 *   full-screen surface would have to sit *over* the HUD to receive anything,
 *   and then it would swallow every tap meant for a panel. The canvas is already
 *   under everything, `.hud` is already `pointer-events: none`, and anything in
 *   the HUD that wants input already turns it back on for itself
 *   (`hud/panels.ts`'s first rule) — so a finger that lands on a button reaches
 *   the button, a finger that lands on the journal reaches the journal, and a
 *   finger that lands on the world reaches the canvas and becomes a stick. The
 *   brief's *"a touch that starts on a button never becomes a stick"* is the
 *   platform's own hit test rather than a check this file makes.
 * - **Each stick owns a pointer id**, so walking and looking at once is two
 *   fingers and not a fight. The browser implicitly captures a touch pointer to
 *   the element it went down on, which is what lets a thumb wander over a button
 *   mid-drag without pressing it.
 * - **Nothing is drawn that a player has to aim at.** The sticks are wherever
 *   the thumb landed; the only mark is a faint ring under the finger, which
 *   exists so that a drag that did nothing can be told apart from a screen that
 *   did not hear it.
 *
 * The pure halves — the stick's arithmetic, the look rate, the mode question and
 * the button table — are exported and checked in `dev/selfcheck/input.ts`. The
 * numbers and the alternatives that were turned down are in
 * `docs/decisions/input.md`.
 */

/**
 * `?touch=` — the mode, forced.
 *
 * Read here rather than in `session/params.ts` on that file's own terms (*"the
 * older or newer ones live where they are read"*), and it is not the on/off flag
 * every other parameter in this client is: **absent means ask the device.** So
 * the parse is a tri-state and cannot borrow the shared spelling.
 */
const TOUCH_PARAM = "touch";

/**
 * How far a thumb has to move before it means anything, and where the walk is at
 * full speed, in CSS pixels.
 *
 * PROVISIONAL, and the two ends are answering different questions. Twelve is
 * roughly the slop in a thumb that thinks it is holding still — smaller and the
 * player drifts while reading, larger and the stick feels like it sticks. Sixty
 * is about a centimetre of travel on a phone, which is what a thumb can reach
 * without the hand moving; a longer throw would be more precise and would have
 * the player re-planting their thumb to keep walking.
 *
 * They are a *distance*, not a fraction of the screen, because a thumb is the
 * same size on every phone (`docs/decisions/input.md`).
 *
 * Exported so the self-checks can press on the *rule* rather than on the
 * numbers: a check written against a literal 12 would have to be edited every
 * time the owner retunes the feel, which is the fastest way to end up with a
 * check nobody trusts (`dev/selfcheck/input.ts`).
 */
export const STICK_DEAD_PX = 12;
export const STICK_FULL_PX = 60;

/**
 * How far out the walk becomes a run: a fraction of full deflection.
 *
 * Sprint has no key on a touchscreen and no button either, and neither absence
 * is a shortcut — a modifier wants a second finger, and a button wants a thumb
 * that is already holding the stick. So it is the edge of the stick, which is the
 * one gesture a thumb can make while walking. High enough that an ordinary walk
 * never trips it, low enough to be reachable without leaving the ring.
 *
 * Exported with the two lengths above and for their reason.
 */
export const SPRINT_DEFLECTION = 0.85;

/**
 * How far the view turns across one sweep of the screen's short side, in radians.
 *
 * Half a turn, which in portrait — where the short side *is* the width — is the
 * brief's *"a full-width swipe is about 180 degrees"* exactly.
 *
 * **The short side rather than the width, so that the feel does not change when
 * the phone is turned.** A rate taken off the width is twice as fast in portrait
 * as in landscape on the same device, and a player who rotates the phone to see
 * more of the world would find the camera had changed gearing underneath them.
 * Taken off the short side, the two orientations are the same gesture — and in
 * landscape a full-width swipe is simply more than half a turn, which is what
 * more room to swipe should mean.
 *
 * Yaw and pitch share the rate, so a diagonal drag turns and tilts by the same
 * amount per pixel. The pitch clamp is the player controller's, unchanged
 * (`player/controller.ts`).
 */
const LOOK_TURN_RADIANS = Math.PI;

/**
 * How much of the top of the screen a stick may not start in, in CSS pixels.
 *
 * The readout's own box, read off `styles.css`: twelve of margin, eight of
 * padding at each end, and six lines at thirteen pixels and 1.45 line-height —
 * 141, rounded up to a multiple of eight. PROVISIONAL for the obvious reason
 * that it is a second copy of a stylesheet's arithmetic; what it buys is that a
 * finger put down to read the numbers does not also walk the player, which on a
 * phone is most of the top-left corner of the frame.
 */
const STICK_TOP_BAND_PX = 144;

/**
 * How long a finger must rest on the ⋯ before it opens the debug window, in
 * milliseconds.
 *
 * The window is `?debug=1`'s and it has no button of its own on purpose: a
 * playtester who fat-fingers it must not meet the log, which is the rule the
 * backquote is bound under (`input/controls.ts`'s `DEBUG_KEYS`). A long press on
 * the row that is already the client's "and the rest" is the touch spelling of
 * a key nobody finds by accident. Six hundred milliseconds is the platform
 * convention for a press that is not a tap.
 */
const LONG_PRESS_MS = 600;

/** What the interact button says with nothing in front of the player. */
const REACH_LABEL = "use";

/** What the ⋯ button says. */
const MORE_LABEL = "···";

/** Which stick a touch became, or neither. */
export type TouchStick = "move" | "look";

/** The move stick's reading: a walk, and whether it is a run. */
export interface StickReading {
  readonly forward: number;
  readonly strafe: number;
  readonly sprint: boolean;
}

/** One on-screen button: what it says, what it fires, and which row it is in. */
export interface TouchButton {
  /** The verb it presses. Every one of them is in `input/controls.ts`'s table. */
  readonly verb: TouchVerb;

  /** What it says. Lowercase and plain, like every other string this client shows. */
  readonly label: string;

  /**
   * Which row it is in, bottom-up: `reach` is under the thumb, `work` is the row
   * above it, and `more` is what the ⋯ reveals.
   */
  readonly row: "reach" | "work" | "more";

  /** Whether it is one of the two large ones. */
  readonly large?: boolean;

  /** Whether it only exists in a `?dev=1` session. */
  readonly dev?: boolean;
}

/**
 * Every on-screen button, in the order they are built.
 *
 * **The ⋯ is not in this list**, and its absence is the list's invariant: every
 * entry here names a verb `Controls` can press and `main.ts` already drains
 * (`input/controls.ts`'s `TOUCH_VERBS`), and the ⋯ opens a row rather than doing
 * anything in the world. A check reads exactly that — *no button is wired to a
 * latch nobody consumes* — and it could not, if a piece of chrome were in here
 * with them.
 *
 * The order is the order a thumb reaches them. The two verbs a player presses
 * every minute are the two large ones nearest the corner; the two aimed verbs
 * are the row above; everything that opens a panel, passes time or hands the
 * session over is behind the ⋯, because none of it is pressed while walking.
 */
export const TOUCH_BUTTONS: readonly TouchButton[] = [
  { verb: "interact", label: REACH_LABEL, row: "reach", large: true },
  { verb: "jump", label: "jump", row: "reach", large: true },
  { verb: "dig", label: "dig", row: "work" },
  // **Beside the dig rather than under `⋯`**, and it is the same argument the
  // work row was drawn under: a verb somebody presses while a guard is walking
  // at them is a verb that cannot be two taps away. The desktop's left button
  // decides between the two by what the crosshair is on (`main.ts`); a thumb has
  // no crosshair, so it gets both.
  { verb: "strike", label: "strike", row: "work" },
  // The bow, beside the blade and on the same row: a thumb has no crosshair to
  // disambiguate two verbs with, so the aimed pair is two buttons.
  { verb: "loose", label: "loose", row: "work" },
  { verb: "gather", label: "gather", row: "work" },
  // **One button where three were** (chunk U-3). The hands, the map and the
  // journal are pages of one screen now, so the row that used to offer three
  // ways into three overlays offers the one way into the screen — and what
  // chooses a page inside it is the tab strip, which is a thing a thumb presses
  // directly. Their VERBS are still in `TOUCH_VERBS` because their keys are
  // still bound; what went is three buttons that each said one page's name.
  { verb: "menu", label: "menu", row: "more" },
  { verb: "camp", label: "camp", row: "more" },
  { verb: "wait", label: "wait", row: "more" },
  // The pass-time family's third (chunk Fx-5), on the row its siblings are on
  // and for their reason: it is pressed standing still. **It is the one button
  // here that is pressed while the screen is black** — the ⋯ row is drawn after
  // the HUD's blackout in document order, so a thumb can still reach it, which
  // is what makes the option in the dark an option on a phone as well.
  { verb: "rest", label: "rest", row: "more" },
  { verb: "drop", label: "drop", row: "more" },
  // The hands panel's own two verbs, on the row the panel is opened from: what
  // they act on is what the player is carrying rather than anything out in the
  // world, so there is nothing to aim and nothing to stand in front of.
  { verb: "wear", label: "put on", row: "more" },
  { verb: "doff", label: "take off", row: "more" },
  // The corner button a desktop session has, moved indoors: bottom-left is where
  // a thumb walks now, and a session-saving button under the move stick is a
  // replay file downloaded every time somebody sets off westward (`styles.css`
  // hides the corner one in touch mode).
  { verb: "export", label: "save", row: "more" },
  // The trace view, which is bound in every session and listened to in none but
  // a `?dev=1` one (`main.ts`). Drawn under the same condition, so the row does
  // not offer a player a button that does nothing.
  { verb: "trace", label: "trace", row: "more", dev: true },
];

/**
 * How a panel says it is closed, on a touchscreen.
 *
 * Every panel in this client tells the reader which key shuts it — *"F
 * closes"*, *"tap ··· then menu to close"* — and on a phone every one of those
 * sentences is about a key nobody has and a wheel nobody turns. What closes
 * them there is the button that opened them, because the ⋯ row deliberately
 * stays open when a panel does: the way out is where the way in was, under the
 * same thumb.
 *
 * **Composed from {@link TOUCH_BUTTONS} rather than typed out at each panel**,
 * which is the whole reason this is a function. The hint names a button, and a
 * button's label is a string this file owns — so a renamed *hands* button would
 * otherwise leave the inventory panel telling a player to press something that
 * is not there, and nothing would say so.
 *
 * A verb on the ⋯ row needs the row named first; one of the four in front does
 * not, which is why the market board and a chronicle read *"tap use to close"*
 * — they are closed by the same verb that opened them.
 */
export function touchCloseHint(verb: TouchVerb): string {
  const button = TOUCH_BUTTONS.find((entry) => entry.verb === verb);

  if (button === undefined) {
    // Unreachable: the table is a constant in this file and the callers name
    // verbs that are in it. Checked rather than asserted away for the reason the
    // reach button is (`TouchControls`), and it answers with the honest thing —
    // a panel that says nothing about closing rather than one that says *tap
    // undefined*.
    return "";
  }

  return button.row === "more"
    ? `tap ${MORE_LABEL} then ${button.label} to close`
    : `tap ${button.label} to close`;
}

/**
 * What one button says, for a hint that names it in the middle of a sentence.
 *
 * {@link touchCloseHint}'s smaller sibling and it exists for one caller: the
 * inventory panel's hint names *two* buttons — the one that closes it and the
 * one that empties a hand — and only the first of them is a closing. Same rule
 * as its sibling: the label is read out of {@link TOUCH_BUTTONS} rather than
 * typed twice.
 */
export function touchButtonLabel(verb: TouchVerb): string {
  return TOUCH_BUTTONS.find((entry) => entry.verb === verb)?.label ?? "";
}

/**
 * Returns what `?touch=` asked for, or `null` when it asked for nothing.
 *
 * Tri-state, unlike every other flag this client reads, and that is the whole
 * point: the ordinary session is *detected*, and the parameter exists so the
 * mode can be turned on at a desk (`?touch=1`, which is how the proof script and
 * the owner look at it) and off on a device that reports a touchscreen it does
 * not really have (`?touch=0`).
 *
 * The off spellings are `session/params.ts`'s, restated rather than shared for
 * that file's own stated reason — six lines against dragging a module in — and
 * because the shape here is different: an absent parameter is not `false`.
 */
export function touchRequested(search: string): boolean | null {
  const raw = new URLSearchParams(search).get(TOUCH_PARAM);

  if (raw === null) {
    return null;
  }

  const value = raw.trim().toLowerCase();

  return value !== "0" && value !== "false" && value !== "off";
}

/**
 * Whether this session is played with fingers.
 *
 * **Three answers, in order of how deliberate each one is.** The link wins where
 * it says anything; then the setting, where the player has chosen one; then the
 * device, which is asked twice because neither question is sufficient on its
 * own: `pointer: coarse` is the honest one and is what a phone and a tablet
 * answer, and `maxTouchPoints` catches the ones whose primary pointer is a mouse
 * but which a hand can still reach — a touchscreen laptop, a Windows tablet in
 * its keyboard.
 *
 * Pure, and it takes all three facts as arguments rather than reading any of
 * them, on `session/params.ts`'s rule: the awkward cases are checked without a
 * browser to set up. `stored` is `null` for *ask the device*, which is what the
 * settings page's *automatic* means and what a browser that will not remember
 * anything answers with — the translation is
 * `hud/title.ts`'s `touchChoiceSaysYes`, beside the key it is remembered under,
 * because the setting belongs to the page that sets it and this file has no
 * storage in it (chunk U-6).
 */
export function touchMode(
  search: string,
  coarsePointer: boolean,
  touchPoints: number,
  stored: boolean | null = null,
): boolean {
  return touchRequested(search) ?? stored ?? (coarsePointer || touchPoints > 0);
}

/**
 * Reads the device, the way {@link touchMode} wants to be asked.
 *
 * One line, and it exists so that the two spellings live beside the function
 * that takes them rather than in `main.ts`'s boot sequence — the same reason
 * every other "what is this session" question is a function here rather than an
 * expression there.
 */
export function touchDeviceSaysYes(): { coarsePointer: boolean; touchPoints: number } {
  return {
    coarsePointer: window.matchMedia("(pointer: coarse)").matches,
    touchPoints: navigator.maxTouchPoints,
  };
}

/**
 * Turns a stick's displacement in screen pixels into a walk.
 *
 * Screen y is down and the world's forward is up, which is the one sign flip
 * here. The dead zone is subtracted from the *radius* rather than from each
 * axis, so the direction a thumb sets is the direction it walks — subtracting
 * per axis would snap a diagonal towards the axes.
 *
 * The result is never longer than one, so a diagonal is exactly as fast as a
 * straight line: `PlayerController` only normalises a vector longer than one
 * (`player/controller.ts`), and a stick that answered `(1, 1)` would walk
 * north-east at the speed of a sprint.
 */
export function stickReading(dx: number, dy: number): StickReading {
  const deflection = stickDeflection(dx, dy);

  if (deflection === 0) {
    return { forward: 0, strafe: 0, sprint: false };
  }

  const radius = Math.hypot(dx, dy);

  return {
    forward: (-dy / radius) * deflection,
    strafe: (dx / radius) * deflection,
    sprint: deflection >= SPRINT_DEFLECTION,
  };
}

/**
 * How far the stick is pushed, 0 to 1.
 *
 * The walk's *magnitude* and the knob's position and the sprint's test, all one
 * number — which is what makes the ring honest: a player who has pushed the knob
 * to the rim can see that they are running, because the rim is where the test
 * is.
 */
export function stickDeflection(dx: number, dy: number): number {
  const radius = Math.hypot(dx, dy);

  if (radius <= STICK_DEAD_PX) {
    return 0;
  }

  return Math.min(1, (radius - STICK_DEAD_PX) / (STICK_FULL_PX - STICK_DEAD_PX));
}

/**
 * Turns a look drag in screen pixels into radians of yaw and pitch.
 *
 * Both are negated, for the mouse's reason: positive yaw turns left and positive
 * pitch looks up, and a screen reports right and down as positive. So the world
 * moves *with* the finger — dragging left swings the view left — which is the
 * convention every first-person game on a touchscreen uses and the opposite of
 * the one a map panel uses (`map/panel.ts` drags the paper, not the view).
 *
 * `shortSide` is the smaller of the viewport's two dimensions; see
 * {@link LOOK_TURN_RADIANS} for why it is that rather than the width.
 */
export function lookDelta(
  dx: number,
  dy: number,
  shortSide: number,
): { readonly yaw: number; readonly pitch: number } {
  // A viewport with no size is a frame that has not been laid out; answering
  // with a division by zero would put a NaN into the camera, and a NaN in a
  // rotation never comes out again.
  const rate = shortSide > 0 ? LOOK_TURN_RADIANS / shortSide : 0;

  return { yaw: -dx * rate, pitch: -dy * rate };
}

/**
 * Which stick a touch at this point becomes, or `null` for neither.
 *
 * Halves rather than corners, and the whole screen below the readout rather than
 * a pad in the bottom corner: a thumb that has to find an invisible target is a
 * thumb that misses it, and the two things a player does constantly are walk and
 * look. The top band is the readout's, and it is excluded so that a finger put
 * down to read a number does not also set off walking
 * ({@link STICK_TOP_BAND_PX}).
 */
export function stickFor(x: number, y: number, width: number): TouchStick | null {
  if (y < STICK_TOP_BAND_PX) {
    return null;
  }

  return x < width / 2 ? "move" : "look";
}

/** What `TouchControls` needs to exist. */
export interface TouchOptions {
  /** The HUD, which the buttons are appended to and which gets the touch class. */
  readonly root: HTMLElement;

  /**
   * Where a stick may start: the canvas.
   *
   * The same element `Controls` takes for its target, and it is passed rather
   * than found for the reason every other reference in this client is passed —
   * a module that went looking for `#viewport` would be markup in two places at
   * once (`index.html`).
   */
  readonly surface: HTMLElement;

  /** Where a walk, a look and a verb go (`input/controls.ts`). */
  readonly controls: Controls;

  /** Whether this is a `?dev=1` session, which decides one button. */
  readonly dev: boolean;
}

/**
 * The touch controls: two invisible sticks, two rows of buttons and a ⋯.
 *
 * Built by `main.ts` in touch mode and not at all otherwise, so a desktop
 * session carries no extra DOM, no extra listeners and no extra branch in the
 * frame loop.
 */
export class TouchControls {
  private readonly root: HTMLElement;
  private readonly surface: HTMLElement;
  private readonly controls: Controls;

  /** The whole layer, so `dispose` can take it off in one. */
  private readonly layer: HTMLElement;

  /** The move stick's ring and the knob inside it, and the look stick's ring. */
  private readonly moveRing: HTMLElement;
  private readonly moveKnob: HTMLElement;
  private readonly lookRing: HTMLElement;

  /** The second row, and the button that reveals it. */
  private readonly moreRow: HTMLElement;
  private readonly moreButton: HTMLButtonElement;

  /** The interact button, which says what it would do. */
  private readonly reachButton: HTMLButtonElement;

  /** What the interact button is currently saying, so an unchanged frame writes nothing. */
  private reaching: string | null = null;

  /** The finger walking, and where it went down. `null` when nobody is walking. */
  private moveId: number | null = null;
  private moveOriginX = 0;
  private moveOriginY = 0;

  /** The finger looking, and where it was last frame. */
  private lookId: number | null = null;
  private lookX = 0;
  private lookY = 0;

  /** The long press on the ⋯, while one is running. */
  private longPress: number | null = null;

  /** Whether the long press fired, so the lift that follows does not also open the row. */
  private longPressed = false;

  constructor(options: TouchOptions) {
    this.root = options.root;
    this.surface = options.surface;
    this.controls = options.controls;

    // *This session is played with thumbs*, said once where the stylesheet can
    // read it. Seven selectors key on it (`styles.css`): the corner save button
    // and the two development controls go, the readout shrinks, and three
    // surfaces placed against a wide frame move clear of the pad on a narrow
    // one.
    this.root.classList.add("hud--touch");

    this.layer = document.createElement("div");
    this.layer.className = "hud__touch";

    this.moveRing = document.createElement("div");
    this.moveRing.className = "hud__touch-ring";
    this.moveRing.hidden = true;

    this.moveKnob = document.createElement("div");
    this.moveKnob.className = "hud__touch-knob";
    this.moveKnob.hidden = true;

    this.lookRing = document.createElement("div");
    this.lookRing.className = "hud__touch-ring hud__touch-ring--look";
    this.lookRing.hidden = true;

    const pad = document.createElement("div");
    pad.className = "hud__touch-pad";

    this.moreRow = this.row("more");
    // Hidden until the ⋯ is pressed. `hidden` rather than a class, because the
    // row is a flex child and a flex child with `hidden` on it still lays out —
    // which is the trap `styles.css` already carries a block about — so the
    // stylesheet gives it a `display` of its own.
    this.moreRow.hidden = true;

    const workRow = this.row("work");

    this.moreButton = this.button(MORE_LABEL, "hud__touch-button hud__touch-button--more");
    this.moreButton.addEventListener("pointerdown", this.handleMoreDown);
    this.moreButton.addEventListener("pointerup", this.handleMoreUp);
    this.moreButton.addEventListener("pointercancel", this.handleMoreCancel);
    this.moreButton.addEventListener("pointerleave", this.handleMoreCancel);
    workRow.append(this.moreButton);

    const reachRow = this.row("reach");
    const reach = reachRow.querySelector<HTMLButtonElement>("[data-verb='interact']");

    if (reach === null) {
      // The table above is a constant in this file, so this cannot happen — and
      // it is checked rather than asserted away because the alternative is a
      // non-null assertion, which would be the one place in this client where a
      // type is claimed rather than proved.
      throw new Error("input/touch.ts: the reach row must carry the interact button");
    }

    this.reachButton = reach;

    // Bottom-up in the frame is top-down in the column: the row a thumb reaches
    // without moving is last.
    pad.append(this.moreRow, workRow, reachRow);
    this.layer.append(this.moveRing, this.moveKnob, this.lookRing, pad);
    this.root.append(this.layer);

    this.surface.addEventListener("pointerdown", this.handlePointerDown);
    this.surface.addEventListener("pointermove", this.handlePointerMove);
    this.surface.addEventListener("pointerup", this.handlePointerUp);
    this.surface.addEventListener("pointercancel", this.handlePointerUp);
    window.addEventListener("blur", this.handleBlur);

    if (!options.dev) {
      // Built and then taken away rather than conditionally built: the table
      // above is the one statement of what the buttons are, and a row assembled
      // two different ways would be two.
      for (const devOnly of this.moreRow.querySelectorAll<HTMLElement>("[data-dev='1']")) {
        devOnly.remove();
      }
    }
  }

  /**
   * Tells the interact button what it would do, or that there is nothing there.
   *
   * Called once a frame from `main.ts` with the verb the aiming prompt is
   * already naming, so the button and the prompt say the same word about the
   * same thing. It writes nothing when the word has not changed, which is
   * `hud/panels.ts`'s rule for anything the frame loop touches.
   *
   * The prompt itself drops its key on a touchscreen — *"talk to Halvard
   * Reedmere"* rather than *"F — talk to Halvard Reedmere"* — because there is no
   * F to press and the button beside it now carries the verb
   * (`witness/interact.ts`'s `promptFor`).
   */
  aimAt(verb: string | null): void {
    if (verb === this.reaching) {
      return;
    }

    this.reaching = verb;
    this.reachButton.textContent = verb ?? REACH_LABEL;
    // Dimmed with nothing in front of the player. Not disabled: pressing it is
    // still how a player finds out there is nothing there, and the refusal line
    // is what says so (`hud/panels.ts`'s `FeedbackLine`).
    this.reachButton.classList.toggle("hud__touch-button--idle", verb === null);
  }

  /** Detaches every listener and takes the layer off the page. */
  dispose(): void {
    this.surface.removeEventListener("pointerdown", this.handlePointerDown);
    this.surface.removeEventListener("pointermove", this.handlePointerMove);
    this.surface.removeEventListener("pointerup", this.handlePointerUp);
    this.surface.removeEventListener("pointercancel", this.handlePointerUp);
    window.removeEventListener("blur", this.handleBlur);
    this.clearLongPress();
    this.layer.remove();
    this.root.classList.remove("hud--touch");
  }

  /** Builds one row of buttons out of {@link TOUCH_BUTTONS}. */
  private row(which: TouchButton["row"]): HTMLElement {
    const element = document.createElement("div");
    element.className = "hud__touch-row";

    for (const entry of TOUCH_BUTTONS) {
      if (entry.row !== which) {
        continue;
      }

      const button = this.button(
        entry.label,
        entry.large === true
          ? "hud__touch-button hud__touch-button--large"
          : "hud__touch-button",
      );

      button.dataset["verb"] = entry.verb;

      if (entry.dev === true) {
        button.dataset["dev"] = "1";
      }

      button.addEventListener("pointerdown", (event) => {
        this.fire(event, entry.verb);
      });
      element.append(button);
    }

    return element;
  }

  /** The anatomy every on-screen button shares. */
  private button(label: string, className: string): HTMLButtonElement {
    const element = document.createElement("button");
    element.type = "button";
    element.className = className;
    element.textContent = label;

    return element;
  }

  /**
   * Presses one button's verb.
   *
   * On `pointerdown` rather than on a click, and both halves of that matter: a
   * verb that waited for the lift would feel like a delay on every press, and
   * the `click` a touchscreen synthesises afterwards is the event this file is
   * trying not to depend on. `preventDefault` is what stops the browser making
   * the compatibility mouse events at all, which is also what keeps a tap on
   * *jump* from arriving at the canvas as a `mousedown` that digs.
   */
  private fire(event: PointerEvent, verb: TouchVerb): void {
    event.preventDefault();
    // The first press is also the tap that starts the session, so a player who
    // opens the map before taking a step is not left reading through the
    // tap-to-play overlay.
    this.controls.requestLock();
    this.controls.press(verb);
  }

  private readonly handlePointerDown = (event: PointerEvent): void => {
    // The tap-to-play overlay goes here rather than on the lift: it is a wall of
    // text over the world, and a player who put a thumb down to walk should be
    // looking at the world by the time they have moved it.
    this.controls.requestLock();

    const wanted = stickFor(event.clientX, event.clientY, window.innerWidth);

    if (wanted === "move" && this.moveId === null) {
      this.moveId = event.pointerId;
      this.moveOriginX = event.clientX;
      this.moveOriginY = event.clientY;
      this.drawMove(0, 0);
      event.preventDefault();

      return;
    }

    if (wanted === "look" && this.lookId === null) {
      this.lookId = event.pointerId;
      this.lookX = event.clientX;
      this.lookY = event.clientY;
      this.drawLook(event.clientX, event.clientY);
      event.preventDefault();
    }
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (event.pointerId === this.moveId) {
      const dx = event.clientX - this.moveOriginX;
      const dy = event.clientY - this.moveOriginY;
      const reading = stickReading(dx, dy);

      this.controls.setStick(reading.forward, reading.strafe, reading.sprint);
      this.drawMove(dx, dy);

      return;
    }

    if (event.pointerId !== this.lookId) {
      return;
    }

    // Per-frame deltas rather than a distance from the origin: a look drag has
    // no origin to return to, exactly as a mouse has none, so what is pushed
    // across is how far the finger moved since the last event. The accumulator
    // on the other side is the mouse's own (`input/controls.ts`).
    const look = lookDelta(
      event.clientX - this.lookX,
      event.clientY - this.lookY,
      Math.min(window.innerWidth, window.innerHeight),
    );

    this.lookX = event.clientX;
    this.lookY = event.clientY;
    this.controls.pushLook(look.yaw, look.pitch);
    this.drawLook(event.clientX, event.clientY);
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (event.pointerId === this.moveId) {
      this.moveId = null;
      this.controls.setStick(0, 0, false);
      this.moveRing.hidden = true;
      this.moveKnob.hidden = true;

      return;
    }

    if (event.pointerId === this.lookId) {
      this.lookId = null;
      this.lookRing.hidden = true;
    }
  };

  /**
   * Lets go of everything when the tab does.
   *
   * A backgrounded tab stops delivering `pointerup`, so a thumb that was walking
   * when the phone rang would still be walking when it came back — the same
   * failure the keys have and the same answer (`input/controls.ts`'s
   * `handleBlur` clears the stick on its own side; this clears the ids that
   * would otherwise still be matching).
   */
  private readonly handleBlur = (): void => {
    this.moveId = null;
    this.lookId = null;
    this.moveRing.hidden = true;
    this.moveKnob.hidden = true;
    this.lookRing.hidden = true;
    this.clearLongPress();
  };

  private readonly handleMoreDown = (event: PointerEvent): void => {
    event.preventDefault();
    this.controls.requestLock();
    this.longPressed = false;
    this.longPress = window.setTimeout(() => {
      this.longPress = null;
      this.longPressed = true;
      this.controls.press("debug");
    }, LONG_PRESS_MS);
  };

  private readonly handleMoreUp = (event: PointerEvent): void => {
    event.preventDefault();
    this.clearLongPress();

    if (this.longPressed) {
      // The window is already open; opening the row on top of it would be one
      // gesture doing two things.
      this.longPressed = false;

      return;
    }

    this.moreRow.hidden = !this.moreRow.hidden;
    this.moreButton.classList.toggle("hud__touch-button--on", !this.moreRow.hidden);
  };

  private readonly handleMoreCancel = (): void => {
    this.clearLongPress();
    this.longPressed = false;
  };

  private clearLongPress(): void {
    if (this.longPress !== null) {
      window.clearTimeout(this.longPress);
      this.longPress = null;
    }
  }

  /**
   * Draws the move stick: a ring where the thumb landed, a knob where it is.
   *
   * The knob is clamped to the ring's edge, so pushing further than full
   * deflection looks like what it is — the stick is already all the way over,
   * and the extra travel is the sprint.
   */
  private drawMove(dx: number, dy: number): void {
    const radius = Math.hypot(dx, dy);
    const at = radius > STICK_FULL_PX ? STICK_FULL_PX / radius : 1;

    this.moveRing.hidden = false;
    this.moveKnob.hidden = false;
    this.moveRing.style.transform = `translate(${this.moveOriginX}px, ${this.moveOriginY}px)`;
    this.moveKnob.style.transform =
      `translate(${this.moveOriginX + dx * at}px, ${this.moveOriginY + dy * at}px)`;
    this.moveKnob.dataset["running"] = stickDeflection(dx, dy) >= SPRINT_DEFLECTION ? "1" : "0";
  }

  /** Draws the look stick: one faint ring under the finger, and nothing else. */
  private drawLook(x: number, y: number): void {
    this.lookRing.hidden = false;
    this.lookRing.style.transform = `translate(${x}px, ${y}px)`;
  }
}
